import React, { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MainButton } from "@/ui/Buttons/MainButton/MainButton";
import memoryComic from "@/assets/images/mainPage_bg3.png";
import mobileMemoryComic from "@/assets/images/mainPage_bg3.mobile.png";
import huggingHeading from "@/assets/images/mainPage_bg2.optimized.png";
import supportProjectImg from "@/assets/images/support_fireflies.png";
import { HomepageBlog } from "@/features/HomepageBlog/HomepageBlog";
import { HomepageChat } from "@/features/HomepageChat/HomepageChat";
import { HomepageSky } from "@/features/HomepageSky/HomepageSky";
import { SkyVisitors } from "@/features/HomepageSky/SkyVisitors";
import { HomepageMemoryWindow } from "@/features/HomepageMemoryWindow/HomepageMemoryWindow";
import {
  HomepageGrowth,
  HomepageWarmth,
} from "@/features/HomepageMechanics/HomepageMechanics";
import { HomepageMascot } from "@/features/HomepageMascot/HomepageMascot";
import { Seo } from "@/components/Seo/Seo";
import { JsonLd } from "@/components/Seo/JsonLd";
import { organizationSchema, websiteSchema } from "@/components/Seo/schemas";
import { DeferredImage } from "@/ui/DeferredImage/DeferredImage";
import cls from "./styles.module.scss";

const DONATION_URL = "https://donatty.com/fireflies/";

function MascotAnchor({ name }: { name: string }) {
  return (
    <span
      id={`mascot-${name}`}
      className={cls.mascotAnchor}
      aria-hidden="true"
    />
  );
}

export const MainPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLElement | null>(null);

  const onCreate = () => navigate("/pets/create");

  const supportSection = (
    <section
      key="support"
      id="home-support"
      className={cls.supportBlock}
      aria-labelledby="support-title"
    >
      <DeferredImage
        src={supportProjectImg}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className={cls.sceneBackground}
      />
      <div className={cls.sceneContent}>
        <p className={cls.eyebrow}>ЧТОБЫ СВЕТА СТАНОВИЛОСЬ БОЛЬШЕ</p>
        <h2 id="support-title">Помоги светлячкам светить</h2>
        <p className={cls.lead}>
          Помоги этому месту оставаться тёплым и расти.
        </p>
        <MainButton
          label={t("common.supportProject")}
          href={DONATION_URL}
          target="_blank"
          rel="noopener noreferrer"
        />
      </div>
      <MascotAnchor name="support" />
    </section>
  );

  return (
    <main className={cls.wrapper} ref={pageRef}>
      <Seo
        title="Светлячки — пространство памяти о любимых питомцах"
        description="Сохрани историю любимого питомца: фотографии, тёплые воспоминания и маленькое окно памяти. Светлячки — бережное пространство, где можно быть рядом."
        canonicalPath="/"
      />
      <JsonLd id="organization" data={organizationSchema()} />
      <JsonLd id="website" data={websiteSchema()} />

      <section
        id="home-welcome"
        className={cls.hero}
        aria-labelledby="home-title"
      >
        <div className={cls.heroContent}>
          <p className={cls.eyebrow}>ДЛЯ ТЕХ, КТО ОСТАЁТСЯ В СЕРДЦЕ</p>
          <h1 id="home-title">{t("mainPage.yourSpace")}</h1>
          <p className={cls.heroDescription}>{t("mainPage.tellYourStory")}</p>
          <div className={cls.actions}>
            <MainButton label="Создать страничку" onClick={onCreate} />
          </div>
          <HomepageMascot pageRef={pageRef} />
        </div>
        <MascotAnchor name="welcome" />
      </section>

      <h2 id="memory-title" className={cls.huggingBanner}>
        <DeferredImage
          src={huggingHeading}
          alt="Как мы обнимаем"
          width={1280}
          height={360}
        />
      </h2>

      <section
        id="home-memory"
        className={cls.memorySection}
        aria-labelledby="memory-title"
      >
        <figure className={cls.comic}>
          <DeferredImage
            src={memoryComic}
            sources={[
              {
                media: "(max-width: 760px)",
                srcSet: mobileMemoryComic,
                width: 2009,
                height: 1893,
              },
            ]}
            width={4017}
            height={3786}
            alt="Иллюстрированная история о поддержке: общение, воспоминания, посвящения и сияние светлячков"
            loading="lazy"
            decoding="async"
          />
        </figure>
        <MascotAnchor name="memory" />
      </section>

      <section
        id="home-sky-discovery"
        className={`${cls.interactiveSection} ${cls.skyDiscovery}`}
        aria-labelledby="sky-discovery-title"
      >
        <HomepageSky />
        <SkyVisitors />
        <MascotAnchor name="sky-discovery" />
      </section>

      <section
        id="home-how-it-works"
        className={`${cls.interactiveSection} ${cls.howSection}`}
        aria-labelledby="how-title"
      >
        <HomepageMemoryWindow />
        <MascotAnchor name="how-it-works" />
      </section>

      <section
        id="home-growth"
        className={`${cls.interactiveSection} ${cls.growthSection}`}
        aria-labelledby="growth-title"
      >
        <HomepageGrowth />
        <MascotAnchor name="growth" />
      </section>

      <section
        id="home-warmth"
        className={`${cls.interactiveSection} ${cls.warmthSection}`}
        aria-labelledby="warmth-title"
      >
        <HomepageWarmth />
        <MascotAnchor name="warmth" />
      </section>

      <section
        id="home-blog"
        className={cls.interactiveSection}
        aria-labelledby="blog-title"
      >
        <HomepageBlog />
        <MascotAnchor name="blog" />
      </section>

      {supportSection}

      <section
        id="home-community"
        className={cls.communitySection}
        aria-label="Чат тепла"
      >
        <HomepageChat />
        <MascotAnchor name="community" />
      </section>

      <section
        id="home-questions"
        className={`${cls.darkSection} ${cls.questionsSection}`}
        aria-labelledby="questions-title"
      >
        <div className={cls.copy}>
          <h2 id="questions-title">Остались вопросы?</h2>
          <p className={cls.lead}>
            Собрали ответы о страницах памяти и механиках сайта.
          </p>
          <Link className={cls.textLink} to="/faq">
            Все вопросы и ответы <span aria-hidden="true">↗</span>
          </Link>
        </div>
        <MascotAnchor name="questions" />
      </section>
    </main>
  );
};
